/**
 * Age bands — the hotel's Adult/Child/Infant age boundaries, plus the per-variant
 * birth-date validation ranges and date-picker bounds derived from them.
 *
 * The band comes from a DaVinci BA hint (`"…Kind muss zwischen 4 und 12 liegen"`),
 * which only appears on a FAILED (670) BA — see the band-probe (services/api/davinci/
 * bandProbe.ts). When no band is available we fall back to the team-confirmed defaults.
 *
 * Pure + unit-tested. See claude/specs/ChildVsInfant/IMPLEMENTATION-SPEC.md §6.
 */

import { MAX_AGE } from './validation';
import type { TravelerVariant } from './travelerVariant';

export interface AgeBands {
  /** Highest age still classified as an infant (inclusive). */
  infantMax: number;
  /** Lowest age classified as a child (inclusive). */
  childMin: number;
  /** Highest age classified as a child (inclusive). */
  childMax: number;
  /** Lowest age classified as an adult (inclusive). */
  adultMin: number;
  /** Where the band came from. */
  source: 'probe' | 'fallback';
}

/**
 * Fallback bands when no parseable hint is available (team-confirmed):
 * infant 0–2 (≤2y364d), child 3–17, adult ≥18.
 */
export const FALLBACK_BANDS: AgeBands = {
  infantMax: 2,
  childMin: 3,
  childMax: 17,
  adultMin: 18,
  source: 'fallback',
};

/**
 * Matches the system-generated child band hint, e.g.
 * "Das Alter für den Teilnehmertyp Kind muss zwischen 4 und 12 liegen."
 * Anchored to a `Kind`/`Alter` context so an unrelated "zwischen N und M" (a time or price
 * range in some other hint) can't be misread as an age band.
 */
const CHILD_BAND_HINT_PATTERN = /(?:Alter|Kind)[\s\S]*?zwischen\s+(\d+)\s+und\s+(\d+)/i;

/**
 * Scan BA hints for the child band. Returns `{ childMin, childMax }` or `null` when
 * no parseable band is present (successful 712 BAs carry only payment hints).
 *
 * Guards against a degenerate band (`childMin >= childMax`) by returning `null` so the
 * caller falls back to {@link FALLBACK_BANDS}.
 *
 * The band is HOTEL-LEVEL: DaVinci returns a single child-band hint per booking and Player's
 * classification is global, so one band applies to the whole booking — there is no per-room
 * band concept on this platform (a single `ageBands` store ref is correct).
 */
export function parseAgeBandFromHints(
  hints: Array<{ Info?: string }> | undefined | null
): { childMin: number; childMax: number } | null {
  if (!hints) return null;

  for (const hint of hints) {
    const match = hint.Info?.match(CHILD_BAND_HINT_PATTERN);
    if (!match) continue;

    const childMin = Number(match[1]);
    const childMax = Number(match[2]);

    if (!Number.isFinite(childMin) || !Number.isFinite(childMax)) continue;
    if (childMin >= childMax) {
      console.warn(
        `[ageBands] Discarding degenerate child band (min ${childMin} >= max ${childMax}); using fallback.`
      );
      return null;
    }

    return { childMin, childMax };
  }

  return null;
}

/**
 * Turn a parsed `{ childMin, childMax }` into full bands:
 * infant = below child-min, adult = above child-max.
 */
export function deriveBands(raw: { childMin: number; childMax: number }): AgeBands {
  return {
    infantMax: raw.childMin - 1,
    childMin: raw.childMin,
    childMax: raw.childMax,
    adultMin: raw.childMax + 1,
    source: 'probe',
  };
}

/**
 * Inclusive age range for a variant, used for validation + the "why" hint.
 *
 * - `adult`:        `[adultMin, MAX_AGE]`
 * - `under18adult`: `[childMax + 1, 17]` — EMPTY when `childMax >= 17` (the fallback
 *                   case). An empty range (minAge > maxAge) signals the loose
 *                   "< 18 at check-in" rule; callers must NOT render `[18, 17]`.
 * - `child`:        `[childMin, childMax]` when the passenger is hotel-classified;
 *                   `[0, childMax]` when not (see `classified` below).
 * - `infant`:       `[0, infantMax]`
 *
 * `classified` — whether this passenger carries a hotel `hotelClass`. In binary/plain
 * mode (no classification) `deriveVariant` can only return `'adult'`/`'child'`, never
 * `'infant'`, so a `'child'` there means "any non-adult" and must accept 0–2 year olds
 * (the pre-feature behaviour). Only the `child` lower bound depends on this; pass `false`
 * for unclassified passengers. See IMPLEMENTATION-SPEC §10.
 */
export function ageRangeForVariant(
  variant: TravelerVariant,
  bands: AgeBands,
  classified = true
): { minAge: number; maxAge: number } {
  switch (variant) {
    case 'adult':
      return { minAge: bands.adultMin, maxAge: MAX_AGE };
    case 'under18adult':
      return { minAge: bands.childMax + 1, maxAge: 17 };
    case 'child':
      return { minAge: classified ? bands.childMin : 0, maxAge: bands.childMax };
    case 'infant':
      return { minAge: 0, maxAge: bands.infantMax };
  }
}

/** True when a variant's age range is empty (only the under18adult fallback case). */
export function isEmptyRange(range: { minAge: number; maxAge: number }): boolean {
  return range.minAge > range.maxAge;
}

/**
 * Convert a variant's age range to calendar `minDate`/`maxDate` (inclusive) relative
 * to `checkIn`, for the date-picker bounds.
 *
 * For an age range `[minAge, maxAge]` evaluated at check-in:
 *   - `maxDate` (latest birth date / youngest person) = `checkIn − minAge years`
 *   - `minDate` (earliest birth date / oldest person) = `checkIn − (maxAge + 1) years + 1 day`
 *
 * For the empty `under18adult` fallback range (band unknown), the rule is the loose
 * "< 18 at check-in" — i.e. effective range `[0, 17]` (no lower age bound).
 *
 * NOTE: this deviates from IMPLEMENTATION-SPEC §6's empty-range note
 * (`{ minDate: undefined, maxDate: checkIn − 18y − 1d }`), which is inverted — it would
 * force age ≥ 18, contradicting §0.3's "< 18 at check-in". The general formula below is
 * the mathematically correct bound for "younger than 18 at check-in".
 */
export function deriveBirthDateBounds(
  variant: TravelerVariant,
  bands: AgeBands,
  checkIn: Date,
  classified = true
): { minDate?: Date; maxDate?: Date } {
  let { minAge, maxAge } = ageRangeForVariant(variant, bands, classified);

  // Empty under18adult range → loose "< 18 at check-in" (effective [0, 17]). Gated to
  // under18adult: an empty range on another variant (e.g. infant [0,-1] when childMin===0)
  // keeps its inverted bounds so the picker offers no date.
  if (variant === 'under18adult' && minAge > maxAge) {
    minAge = 0;
    maxAge = 17;
  }

  return {
    maxDate: subtractYearsDays(checkIn, minAge, 0),
    minDate: subtractYearsDays(checkIn, maxAge + 1, -1),
  };
}

/** `date − years − days`, returning a new Date (negative `days` adds days). */
function subtractYearsDays(date: Date, years: number, days: number): Date {
  return new Date(date.getFullYear() - years, date.getMonth(), date.getDate() - days);
}
